/**
 * 이상한 방 - 포인터 → 그리드 셀 변환 (표면 스냅)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 3.2절(입력 UX - 표면 스냅) · 3절 · 3.1절
 *
 * ## 이 파일의 핵심 주장
 *
 * 내부 표현은 3D 절대좌표지만(3절), **입력은 표면 스냅을 기본값으로 한다**(3.2절).
 * 포인터가 책상을 가리키면 자동으로 그 위 칸에 붙고, 수정키를 눌러야 자유 높이가 된다.
 *
 * 이유는 두 가지이며 둘 다 게임 디자인이지 편의 기능이 아니다.
 *
 * 1. **꾸미기 페이즈가 1분이다.** z축(높이) 조작 비용이 곧 스트레스가 된다.
 *    매번 높이를 지정해야 한다면 1분 안에 아무도 방을 꾸미지 못한다.
 *
 * 2. **공중부양을 "할 수 있지만 일부러 해야 하는 것"으로 만든다.**
 *    이 게임은 공중부양을 버그가 아니라 콘텐츠로 취급한다(원칙 2, 컨셉시트 5절).
 *    하지만 그건 *의도한* 공중부양일 때의 이야기다 -
 *    **실수로 뜬 것은 버그로 보이지만, 일부러 띄운 것은 뻘짓이다.**
 *    기본값이 표면 스냅이면 떠 있는 물건은 100% 누군가 수정키를 눌러 만든 것이 되고,
 *    "저 화분 왜 떠 있냐"가 불평이 아니라 웃음이 된다.
 *    자유 높이를 기본값으로 두는 순간 이 구분이 통째로 사라진다.
 *
 * ## 책임 경계
 *
 * 이 파일은 **"어느 셀을 가리키고 있는가"**만 답한다.
 * "거기에 놓을 수 있는가"는 `core/verbs.ts`의 `canPlace`가 판정한다(경계·셀 점유).
 * 둘을 섞으면 고스트가 조용히 다른 칸으로 옮겨가 "왜 여기 안 놓이지"가 발생한다.
 * 따라서 여기서는 셀을 **보정하지 않고 있는 그대로** 돌려주고,
 * 방을 완전히 벗어난 경우에만 `null`(가리키는 것이 없음)을 반환한다.
 *
 * ## 상태 변경 규약
 *
 * `ctx.raycaster`를 제자리 변경한다(렌더 레이어 한정 예외). 게임 상태는 읽기만 한다.
 */

import * as THREE from 'three'
import {
  CELL_SIZE_METERS,
  type CatalogEntry,
  type Cell3,
  type InstanceId,
  type ObjectInstance,
  type RoomBounds,
  type RoomState,
} from '../core/types'
import { rotatedFootprint, worldToCell } from '../core/grid'
import type { Catalog } from '../core/room'
import { getInstanceIdFromObject3D } from '../render/objectView'
import type { SceneCtx } from '../render/scene'

/** NDC(정규화 장치 좌표)는 [-1, 1] 구간이다. 픽셀 비율 → NDC 변환에 쓰는 계수. */
const NDC_RANGE_SCALE = 2

/** 바닥 평면의 높이(미터). 그리드 y=0 셀의 아랫면이자 방의 바닥이다. */
const FLOOR_PLANE_Y = 0

/** 위쪽을 가리키는 축. y-up 규약은 `core/types.ts`의 `Cell3` 주석에서 온다. */
const UP_AXIS = Object.freeze({ x: 0, y: 1, z: 0 })

export type PickResult = Readonly<{
  /** 가리키고 있는 그리드 셀. 배치 가능 여부는 판정하지 않는다 - `canPlace`의 몫이다. */
  cell: Cell3
  /** 레이가 기존 오브젝트에 맞았다면 그 인스턴스. 선택·제거 경로가 이 값을 쓴다. */
  instanceId?: InstanceId
  /**
   * 이 셀이 표면(바닥 또는 다른 오브젝트의 윗면)에 붙여 계산됐는가.
   *
   * 바닥도 `true`다. 이 플래그의 의미는 "오브젝트 위인가"가 아니라
   * **"자유 높이 모드가 아닌가"**이며, 호출부는 이걸로 의도적 공중부양을 구분한다.
   * 바닥에 놓는 것은 공중부양이 아니므로 바닥을 `false`로 두면 구분이 뒤집힌다.
   */
  isSurfaceSnap: boolean
}>

/** 레이가 맞힌 오브젝트와 그 판정에 필요한 부속 정보. */
type InstanceHit = Readonly<{
  distance: number
  obj: ObjectInstance
  entry: CatalogEntry
  point: THREE.Vector3
}>

/** 수평 평면과의 교차 결과. */
type PlaneHit = Readonly<{ distance: number; point: THREE.Vector3 }>

/**
 * 포인터 이벤트를 NDC로 바꾼다.
 *
 * 캔버스가 아직 레이아웃되지 않았거나 숨겨져 있으면 rect 크기가 0이 되고,
 * 그대로 나누면 NaN이 흘러들어가 `worldToCell`이 던진다.
 * 조용히 0으로 만들지 않고 여기서 "가리키는 것 없음"으로 끊는다.
 */
function pointerToNdc(ev: PointerEvent, element: HTMLElement): THREE.Vector2 | null {
  const rect = element.getBoundingClientRect()
  if (rect.width <= 0 || rect.height <= 0) return null

  return new THREE.Vector2(
    ((ev.clientX - rect.left) / rect.width) * NDC_RANGE_SCALE - 1,
    -(((ev.clientY - rect.top) / rect.height) * NDC_RANGE_SCALE) + 1,
  )
}

/**
 * 레이에 맞은 오브젝트 중 가장 가까운 것을 찾는다.
 *
 * 방 셸·그리드 오버레이·배치 고스트는 인스턴스 id가 없으므로 자연히 걸러진다.
 * 별도의 제외 목록을 두지 않는 이유이며, 렌더 레이어가 메시를 어떻게 쪼개든 이 필터는 깨지지 않는다.
 * (`getInstanceIdFromObject3D`가 부모까지 거슬러 올라가므로 방향 표식 자식에 맞아도 잡힌다 -
 *  4.2절 부수 요건대로 비대칭 소품에는 표식이 붙기 때문에 이 성질이 필수다.)
 */
function findNearestInstanceHit(
  ctx: SceneCtx,
  state: RoomState,
  catalog: Catalog,
): InstanceHit | null {
  // Raycaster는 월드 행렬을 갱신해 주지 않는다(Three.js 확인 완료).
  // 갱신은 보통 `renderer.render()`가 대신 해 주지만, 포인터 이벤트는 프레임 사이에 도착하므로
  // `syncObjectViews`가 방금 추가·이동시킨 메시는 아직 낡은 행렬을 들고 있을 수 있다.
  // 그 상태로 레이캐스트하면 새 소품이 **원점에 있는 것으로** 잡혀
  // "엉뚱한 칸이 집히는" 조용한 오작동이 된다. 값싼 순회이므로 여기서 스스로 보장한다.
  ctx.scene.updateMatrixWorld()

  // Three.js는 거리 오름차순으로 정렬해 돌려준다.
  const intersections = ctx.raycaster.intersectObject(ctx.scene, true)

  for (const intersection of intersections) {
    const id = getInstanceIdFromObject3D(intersection.object)
    if (id === undefined) continue

    // 씬이 상태보다 한 프레임 뒤처져 이미 제거된 오브젝트를 들고 있을 수 있다.
    // 진실은 상태 쪽이므로 그런 교차는 없는 것으로 친다.
    const obj = state.objects.get(id)
    if (obj === undefined) continue

    const entry = catalog.get(obj.catalogId)
    // 모르는 catalogId는 데이터 불일치다. 여기서 던지지 않는 이유:
    // 이 경로는 포인터 이동마다 도는 핫패스라 한 번의 데이터 오류가 앱 전체를 못 쓰게 만든다.
    // 하드 실패는 `room.ts`의 `buildOccupancy`가 이미 담당한다 -
    // 배치·검증이 먼저 터지므로 오류가 묻히지 않는다.
    if (entry === undefined) continue

    return { distance: intersection.distance, obj, entry, point: intersection.point }
  }

  return null
}

/**
 * 레이와 지정 높이의 수평 평면의 교차점.
 * 카메라 뒤쪽이거나 레이가 평면과 평행하면 `null`.
 */
function intersectHorizontalPlane(ctx: SceneCtx, worldY: number): PlaneHit | null {
  const plane = new THREE.Plane(new THREE.Vector3(UP_AXIS.x, UP_AXIS.y, UP_AXIS.z), -worldY)
  const point = new THREE.Vector3()

  if (ctx.raycaster.ray.intersectPlane(plane, point) === null) return null

  return { distance: ctx.raycaster.ray.origin.distanceTo(point), point }
}

/** 셀의 x·z가 방 안에 있는가. 높이(y)는 검사하지 않는다 - 그건 `isInBounds`의 몫이다. */
function isColumnInsideRoom(cell: Cell3, bounds: RoomBounds): boolean {
  return cell.x >= 0 && cell.x < bounds.sizeX && cell.z >= 0 && cell.z < bounds.sizeZ
}

/**
 * 바닥 히트. 무한 평면이므로 **방 안쪽 구간만** 바닥으로 인정한다.
 * 그러지 않으면 수평에 가까운 시선에서 방 밖 수십 미터 지점이 "바닥"으로 잡힌다.
 */
function findFloorHit(ctx: SceneCtx, bounds: RoomBounds): { hit: PlaneHit; cell: Cell3 } | null {
  const hit = intersectHorizontalPlane(ctx, FLOOR_PLANE_Y)
  if (hit === null) return null

  const column = worldToCell(hit.point.x, hit.point.y, hit.point.z)
  const floorCell: Cell3 = { x: column.x, y: 0, z: column.z }

  if (!isColumnInsideRoom(floorCell, bounds)) return null

  return { hit, cell: floorCell }
}

/**
 * 오브젝트 윗면에 얹히는 셀.
 *
 * 높이는 맞은 지점이 아니라 **오브젝트의 꼭대기**로 잡는다. 옆면을 찍어도 위에 올라가야
 * "책상을 가리키면 그 위에 붙는다"(3.2절)가 성립한다.
 * x·z는 맞은 지점을 쓰되 오브젝트 footprint 안으로 가둔다 -
 * 정확히 모서리를 맞히면 `Math.floor`가 한 칸 바깥을 내놓기 때문이다.
 */
function surfaceCellOnObject(hit: InstanceHit): Cell3 {
  const size = rotatedFootprint(hit.entry.footprint, hit.obj.rot)
  const pointed = worldToCell(hit.point.x, hit.point.y, hit.point.z)

  return {
    x: THREE.MathUtils.clamp(pointed.x, hit.obj.cell.x, hit.obj.cell.x + size.x - 1),
    y: hit.obj.cell.y + size.y,
    z: THREE.MathUtils.clamp(pointed.z, hit.obj.cell.z, hit.obj.cell.z + size.z - 1),
  }
}

/**
 * 자유 높이 모드에서 쓸 높이 평면의 그리드 레벨.
 *
 * 명시값이 없으면 **지금 가리키는 표면의 높이**를 그대로 이어받는다.
 * 이 기본값이 UX의 핵심이다 - 수정키를 누른 순간 높이가 그 자리에서 잠기고,
 * 포인터를 책상 밖으로 밀면 책상 높이 그대로 허공에 남는다.
 * 즉 공중부양이 "따로 배우는 조작"이 아니라 "수정키를 누른 채 밀어내는" 한 동작이 된다.
 */
function resolveFreeHeightLevel(
  explicitLevel: number | undefined,
  bounds: RoomBounds,
  instanceHit: InstanceHit | null,
  floorCell: Cell3 | null,
): number | null {
  if (explicitLevel !== undefined) {
    if (!Number.isInteger(explicitLevel)) {
      throw new RangeError(`pickCell: freeHeightLevel은 정수여야 한다: ${explicitLevel}`)
    }
    // 스크롤 등으로 조절하는 값이라 천장에서 멈추는 편이 자연스럽다. 여기서는 보정이 맞다.
    return THREE.MathUtils.clamp(explicitLevel, 0, bounds.sizeY - 1)
  }

  if (instanceHit !== null) {
    const size = rotatedFootprint(instanceHit.entry.footprint, instanceHit.obj.rot)
    return instanceHit.obj.cell.y + size.y
  }

  if (floorCell !== null) return floorCell.y

  return null
}

/**
 * 포인터가 가리키는 그리드 셀을 판정한다.
 *
 * @param freeHeight 수정키가 눌린 상태인가. `false`(기본 동작)면 표면 스냅이다.
 * @param freeHeightLevel 자유 높이 모드에서 쓸 그리드 높이. 생략하면 가리킨 표면 높이를 이어받는다.
 *   (선택 파라미터라 기존 6인자 호출부는 그대로 동작한다. 스크롤로 높이를 조절하는 UI가 이 값을 넘긴다.)
 * @returns 가리키는 셀이 없으면 `null` - 호출부는 고스트를 감춘다.
 */
export function pickCell(
  ctx: SceneCtx,
  ev: PointerEvent,
  state: RoomState,
  catalog: Catalog,
  bounds: RoomBounds,
  freeHeight: boolean,
  freeHeightLevel?: number,
): PickResult | null {
  const ndc = pointerToNdc(ev, ctx.renderer.domElement)
  if (ndc === null) return null

  ctx.raycaster.setFromCamera(ndc, ctx.camera)

  const instanceHit = findNearestInstanceHit(ctx, state, catalog)
  const floor = findFloorHit(ctx, bounds)

  // 바닥이 오브젝트보다 앞에 있으면 오브젝트는 바닥에 가려 보이지 않는다.
  // 안 보이는 것을 집게 하면 3.1절이 막으려는 "완전 은닉"을 입력 쪽에서 되살리는 셈이다.
  const isObjectVisible =
    instanceHit !== null && (floor === null || instanceHit.distance <= floor.hit.distance)
  const visibleInstanceHit = isObjectVisible ? instanceHit : null

  if (freeHeight) {
    return pickFreeHeight(ctx, bounds, visibleInstanceHit, floor?.cell ?? null, freeHeightLevel)
  }

  if (visibleInstanceHit !== null) {
    return {
      cell: surfaceCellOnObject(visibleInstanceHit),
      instanceId: visibleInstanceHit.obj.id,
      isSurfaceSnap: true,
    }
  }

  if (floor !== null) {
    return { cell: floor.cell, isSurfaceSnap: true }
  }

  return null
}

/**
 * 자유 높이 모드. 높이 평면을 먼저 정하고, 레이와 그 평면의 교차로 x·z를 얻는다.
 *
 * 표면 스냅과 달리 x·z를 오브젝트 footprint 안으로 가두지 않는다 -
 * **가둬 버리면 책상 밖으로 밀어내는 공중부양 자체가 불가능해진다.**
 */
function pickFreeHeight(
  ctx: SceneCtx,
  bounds: RoomBounds,
  instanceHit: InstanceHit | null,
  floorCell: Cell3 | null,
  explicitLevel: number | undefined,
): PickResult | null {
  const level = resolveFreeHeightLevel(explicitLevel, bounds, instanceHit, floorCell)
  if (level === null) return null

  const hit = intersectHorizontalPlane(ctx, level * CELL_SIZE_METERS)
  if (hit === null) return null

  const column = worldToCell(hit.point.x, hit.point.y, hit.point.z)
  const cell: Cell3 = { x: column.x, y: level, z: column.z }
  if (!isColumnInsideRoom(cell, bounds)) return null

  if (instanceHit === null) return { cell, isSurfaceSnap: false }

  return { cell, instanceId: instanceHit.obj.id, isSurfaceSnap: false }
}
